import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useContactModal } from "@/context/ContactModalContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { open } = useContactModal();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A0A0A]/90 backdrop-blur-md border-b border-hairline"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-[88px] flex items-center justify-between">
        <Logo
          size="lg"
          className="!items-center [&>span:first-child]:!text-[30px] [&>span:last-child]:!mt-0.5 [&>span:last-child]:!text-[13px] [&>span:last-child]:!font-medium [&>span:last-child]:!tracking-[0.32em] [&>span:last-child]:!text-gold"
        />

        {/* Desktop nav */}
        <nav
          className="hidden md:flex items-center gap-8 ml-auto mr-8"
          data-testid="primary-nav"
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[14.5px] font-medium tracking-wide transition-colors duration-200 hover:text-gold ${
                  isActive
                    ? "text-gold after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-px after:bg-gold"
                    : "text-offwhite/85"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            onClick={open}
            data-testid="header-cta-button"
            className="px-6 py-2.5 bg-black/20 border border-[#D8BD7A] text-[13px] font-medium tracking-[0.04em] text-[#D8BD7A] backdrop-blur-sm hover:bg-gold hover:border-gold hover:text-[#0A0A0A] transition-all duration-200"
          >
            Start a conversation
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-offwhite"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden bg-[#0A0A0A] border-t border-hairline"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-gold" : "text-offwhite/85"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setMobileOpen(false);
                open();
              }}
              data-testid="mobile-cta-button"
              className="mt-3 self-start px-6 py-3 bg-gold text-[#0A0A0A] text-sm tracking-wide"
            >
              Start a conversation
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
